/**
 * 记忆注入模式。
 *
 * 关键词：沿用现有 `recall.ts` 的命中打分，只把命中的记忆放进请求；
 * 自动：把全部记忆交给对话模型，由模型自行判断哪些与当前对话相关；
 * 持续：每轮都把全部记忆放进请求，模型始终带着这些记忆回答；
 * 关闭：不向模型注入任何记忆。
 *
 * 设置只保存在本机 localStorage，与自动记忆（写入）互不影响。
 */
import type { MemoryEntry } from '../../core/types';
import {
  DEFAULT_MEMORY_BUDGET_CHARS,
  buildMemoryBlock,
  selectMemories,
} from './recall';

export const MEMORY_INJECTION_STORAGE_KEY = 'zxtalk.memoryInjection.v1';
export const MEMORY_INJECTION_CHANGED_EVENT = 'zxtalk:memory-injection-changed';

export const MEMORY_INJECTION_MODES = ['keyword', 'auto', 'continuous', 'off'] as const;
export type MemoryInjectionMode = (typeof MEMORY_INJECTION_MODES)[number];

export const DEFAULT_MEMORY_INJECTION_MODE: MemoryInjectionMode = 'keyword';

/** 自动 / 持续模式一次最多带入的记忆正文长度；超出时按重要度与更新时间保留。 */
export const ALL_MEMORY_BUDGET_CHARS = 12_000;

export const MEMORY_INJECTION_MODE_LABELS: Record<MemoryInjectionMode, string> = {
  keyword: '关键词注入',
  auto: '自动注入',
  continuous: '持续注入',
  off: '关闭注入',
};

export const MEMORY_INJECTION_MODE_HINTS: Record<MemoryInjectionMode, string> = {
  keyword: '仅和模型对话出现记忆标签时才会读取用户的对应记忆（消耗较少）',
  auto: '每次对话模型都会读取所有记忆库，判断记忆是否符合用户需求再决定回答（消耗较多）',
  continuous: '每次对话都会把全部记忆持续注入上下文，模型始终带着这些记忆回答（消耗最多）',
  off: '不会向模型注入任何记忆，已有记忆仍会保存在本地。',
};

const AUTO_MEMORY_HEADER =
  '[记忆]\n以下是用户的全部长期记忆。请判断哪些记忆与当前对话相关，只采用真正相关的内容；无关的记忆不要提及，也不要强行套用。';
const CONTINUOUS_MEMORY_HEADER =
  '[记忆]\n以下是用户的长期记忆，请在每次回答中持续参考，并保持与这些记忆一致。';

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

export function isMemoryInjectionMode(value: unknown): value is MemoryInjectionMode {
  return (
    typeof value === 'string' &&
    (MEMORY_INJECTION_MODES as readonly string[]).includes(value)
  );
}

export function normalizeMemoryInjectionMode(value: unknown): MemoryInjectionMode {
  return isMemoryInjectionMode(value) ? value : DEFAULT_MEMORY_INJECTION_MODE;
}

export function loadMemoryInjectionMode(): MemoryInjectionMode {
  const store = storage();
  if (!store) return DEFAULT_MEMORY_INJECTION_MODE;
  return normalizeMemoryInjectionMode(store.getItem(MEMORY_INJECTION_STORAGE_KEY));
}

export function saveMemoryInjectionMode(mode: MemoryInjectionMode): MemoryInjectionMode {
  const normalized = normalizeMemoryInjectionMode(mode);
  storage()?.setItem(MEMORY_INJECTION_STORAGE_KEY, normalized);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(MEMORY_INJECTION_CHANGED_EVENT, { detail: normalized }),
    );
  }
  return normalized;
}

/**
 * 全量模式的选择：重要度高的优先，同分更新时间新的优先；
 * 单条超出剩余预算时跳过，继续尝试后面的短记忆，尽量多地带入。
 */
export function selectAllMemories(
  memories: readonly MemoryEntry[],
  budgetChars: number = ALL_MEMORY_BUDGET_CHARS,
): MemoryEntry[] {
  const budget = Number.isFinite(budgetChars) ? Math.floor(budgetChars) : 0;
  if (budget <= 0 || memories.length === 0) return [];

  const ranked = [...memories].sort(
    (a, b) =>
      b.importance - a.importance ||
      b.updatedAt - a.updatedAt ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );

  const selected: MemoryEntry[] = [];
  let used = 0;
  for (const memory of ranked) {
    const cost = memory.content.length;
    if (used + cost > budget) continue;
    selected.push(memory);
    used += cost;
  }
  return selected;
}

export interface SelectMemoriesForInjectionInput {
  memories: readonly MemoryEntry[];
  contextText: string;
  mode: MemoryInjectionMode;
  keywordBudgetChars?: number;
  allBudgetChars?: number;
}

export function selectMemoriesForInjection({
  memories,
  contextText,
  mode,
  keywordBudgetChars = DEFAULT_MEMORY_BUDGET_CHARS,
  allBudgetChars = ALL_MEMORY_BUDGET_CHARS,
}: SelectMemoriesForInjectionInput): MemoryEntry[] {
  if (mode === 'off' || memories.length === 0) return [];
  if (mode === 'keyword') {
    return selectMemories(memories, contextText, keywordBudgetChars);
  }
  return selectAllMemories(memories, allBudgetChars);
}

/** 按模式渲染注入块；没有记忆或关闭注入时返回空串。 */
export function buildInjectionMemoryBlock(
  selected: readonly MemoryEntry[],
  mode: MemoryInjectionMode,
): string {
  if (mode === 'off' || selected.length === 0) return '';
  if (mode === 'keyword') return buildMemoryBlock(selected);

  const lines = selected.map((memory, index) => `${index + 1}. ${memory.content}`);
  const header = mode === 'auto' ? AUTO_MEMORY_HEADER : CONTINUOUS_MEMORY_HEADER;
  return [header, ...lines].join('\n');
}
